import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChartCard } from "../../components/observability/index.ts";
import type { ChartData, ChartSeries } from "../../components/index.ts";
import type { DecisionSummary } from "./use-decisions.ts";
import { exactnessMeasure, allDecisionsHref, denyHref } from "./vocab.ts";

/**
 * Allow/deny over time (task 11.6; design 12.2 "allow/deny over time"; 12.3 as a time-series chart
 * with a built-in data-table alternative; 12.4). Folded into the observability kit's `ChartCard`
 * (observability-ui task 4.1): the three allow/deny/fail-closed lines are now presented inside the
 * kit's titled panel surface rather than a bespoke `.dec-overtime` section, so the Decisions screen
 * reads like the Overview and the other section rollouts. This is PRESENTATION-ONLY — the data is
 * exactly the same authoritative, contract-defined summary `series`; nothing in the data layer
 * changed.
 *
 * The series is the summary's authoritative, contract-defined `series`: one point per `step_s`
 * bucket carrying the allow, deny and fail-closed counts. Those three are plotted as three lines
 * through the kit's `ChartCard` (itself over the shared `Chart` wrapper), which brings its
 * always-present data-table alternative and the synced crosshair for free. Fail-closed is drawn as
 * its own distinct line (and labelled in the table) so an operator sees fail-closed movement right
 * on the over-time view, not just in the totals (design 12.3 "fail-closed events highlighted").
 *
 * The whole column's confidence rides on the series' `exactness` (estimated when sampling weighted
 * the counts; design 12.3 "sampled labelled, estimates 'est.'"), so every line carries that measure
 * and the data table shows "est." on its headers when the window was sampled. An empty window shows
 * the chart's honest "no data in range" state rather than a flat zero line.
 *
 * `ChartCard` already surfaces the live/paused state in its header from the shared time-range (so
 * the old bespoke "Live · Ns buckets" note is no longer needed for the live flag); the `step_s`
 * bucket size and the sampled-estimate marker move into the card's footer alongside the two drill
 * affordances. The drills send the operator to the events behind the totals — all decisions, or
 * just the denies — since the summary series itself carries no per-point `drill`.
 */

export interface AllowDenyChartProps {
  readonly series: DecisionSummary["series"];
}

export function AllowDenyChart(props: AllowDenyChartProps): ReactNode {
  const navigate = useNavigate();
  const { series } = props;
  const measure = exactnessMeasure(series.exactness);

  // Build the aligned [xs, allow, deny, fail_closed] from the ordered points. Points are already in
  // time order from the server; we keep that order so the x axis reads left-to-right.
  const points = series.points;
  const xs = points.map((p) => p.t);
  const allow = points.map((p) => p.allow);
  const deny = points.map((p) => p.deny);
  const failClosed = points.map((p) => p.fail_closed);

  const data: ChartData = [xs, allow, deny, failClosed];
  const chartSeries: readonly ChartSeries[] = [
    { label: "Allow", colorVar: "--status-healthy", measure },
    { label: "Deny", colorVar: "--status-error", measure },
    // Fail-closed uses the warn token and a slightly heavier line so it stands apart from an
    // ordinary deny on the plot; its confidence is labelled in the table like the others.
    { label: "Fail-closed", colorVar: "--status-warn", width: 2, measure },
  ];

  // The card footer: the bucket size + sampled-estimate marker (previously the bespoke note), then
  // the two standing-filter drill affordances. ChartCard's `x`/`xIsMillis` default to time/ms,
  // which is exactly what this summary series uses, so folding in loses no axis information.
  const footer = (
    <div className="dec-overtime__footer">
      <span className="dec-section__note">
        {series.step_s}s buckets{measure === "estimate" ? " · sampled (est.)" : ""}
      </span>
      <span className="dec-overtime__drills">
        <button
          type="button"
          className="dec-link"
          onClick={() => navigate(allDecisionsHref())}
          aria-label="Open the events behind all decisions in this window"
        >
          See all decision events
        </button>
        <button
          type="button"
          className="dec-link"
          onClick={() => navigate(denyHref())}
          aria-label="Open the events behind the denied decisions in this window"
        >
          See deny events
        </button>
      </span>
    </div>
  );

  return (
    <ChartCard
      title="Allow / deny over time"
      data={data}
      series={chartSeries}
      height={240}
      empty="No decisions were recorded in this window. Widen the time range, or check that a source is reporting boundary decisions."
      footer={footer}
    />
  );
}
